import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// Perlin 噪声简化版
const noise = (x: number, y: number, seed: number): number => {
  const n = Math.sin(x * 12.9898 + y * 78.233 + seed) * 43758.5453;
  return n - Math.floor(n);
};

export const GLTransitionsRippleDissolve: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // 波纹参数
  const centerX = width / 2;
  const centerY = height / 2;
  const maxRadius = Math.sqrt(centerX * centerX + centerY * centerY);
  const progress = interpolate(frame, [0, fps * 5], [0, 1]);

  // Spring 动画
  const springProgress = spring({
    frame,
    fps,
    config: {
      damping: 20,
      stiffness: 100,
      mass: 0.5,
    },
  });

  // 生成波纹圆环
  const ripples = [];
  const numRipples = 8;
  for (let i = 0; i < numRipples; i++) {
    const rippleProgress = (progress + i * 0.1) % 1;
    const radius = interpolate(rippleProgress, [0, 1], [0, maxRadius]);
    const opacity = interpolate(rippleProgress, [0, 0.5, 1], [0.8, 0.4, 0]);
    const strokeWidth = interpolate(rippleProgress, [0, 1], [4, 1]);

    ripples.push(
      <circle
        key={i}
        cx={centerX}
        cy={centerY}
        r={radius}
        fill="none"
        stroke={`hsla(${200 + i * 20}, 70%, 60%, ${opacity})`}
        strokeWidth={strokeWidth}
        filter="url(#glow)"
      />
    );
  }

  // 生成扭曲网格
  const gridSize = 20;
  const gridLines = [];
  for (let i = 0; i <= gridSize; i++) {
    const pointsH = [];
    const pointsV = [];

    for (let j = 0; j <= gridSize; j++) {
      const x = (i / gridSize) * width;
      const y = (j / gridSize) * height;

      // 计算到中心的距离
      const dx = x - centerX;
      const dy = y - centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const normalizedDist = dist / maxRadius;

      // 波纹扭曲效果
      const waveIntensity = 30 * (1 - normalizedDist) * Math.sin(dist * 0.05 - frame * 0.3);
      const distortX = (dx / dist) * waveIntensity * springProgress;
      const distortY = (dy / dist) * waveIntensity * springProgress;

      pointsH.push(`${x + distortX},${y + distortY}`);
      pointsV.push(`${y + distortY},${x + distortX}`);
    }

    gridLines.push(
      <polyline
        key={`h-${i}`}
        points={pointsH.join(' ')}
        fill="none"
        stroke={`hsla(${220}, 50%, 50%, 0.3)`}
        strokeWidth={1}
      />
    );
  }

  // 中心文字
  const textScale = spring({
    frame: frame - 30,
    fps,
    config: {
      damping: 15,
      stiffness: 200,
      mass: 0.8,
    },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0a0e27',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <svg width={width} height={height}>
        <defs>
          {/* 发光滤镜 */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* 波纹扭曲滤镜 */}
          <filter id="ripple">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.01"
              numOctaves="3"
              result="noise"
              seed={frame * 0.1}
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale={50 * springProgress}
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>

          {/* 径向渐变 */}
          <radialGradient id="centerGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#4ecdc4" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#44a3aa" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#0a0e27" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* 背景渐变 */}
        <rect width={width} height={height} fill="url(#centerGradient)" />

        {/* 扭曲网格 */}
        <g opacity={0.5}>{gridLines}</g>

        {/* 波纹圆环 */}
        {ripples}

        {/* 中心光晕 */}
        <circle
          cx={centerX}
          cy={centerY}
          r={80 * springProgress}
          fill="url(#centerGradient)"
          filter="url(#glow)"
        />

        {/* 中心文字 */}
        <text
          x={centerX}
          y={centerY}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={60}
          fontWeight="bold"
          fill="#ffffff"
          filter="url(#glow)"
          transform={`scale(${textScale})`}
          opacity={textScale}
        >
          GL Transitions
        </text>

        {/* 副标题 */}
        <text
          x={centerX}
          y={centerY + 60}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={24}
          fill="#4ecdc4"
          opacity={interpolate(frame, [60, 90], [0, 1], { extrapolateLeft: 'clamp' })}
        >
          GPU 加速视频转场效果
        </text>
      </svg>
    </AbsoluteFill>
  );
};
